import type { ConstellationEdge, ConstellationNode } from '@diwata/ironvale'
import type { Bond, Familiar } from '@/api/conclave'

// The seam: Conclave's familiar ecosystem → the ironvale Constellation's generic node/edge model.
// This is the same move GraphView makes for Diwa's knowledge graph, but the graph here is the
// keeper's *agents*: a Sovereign at the centre, its Adepts as delegated stars, and the wider web of
// bonded familiars. Kept pure (no React, no fetch) so the topology is unit-testable.

/** The two orbits of the ecosystem — your own household vs. the wider bonded network. */
export type Orbit = 'inner' | 'outer'

/** Grouping key for a star. Doubles as the sector name and the hue lookup. */
export type RosterGroup = 'sovereign' | 'adept' | 'bond' | 'guild'

/**
 * Tier/relation hues (resolved aether brand values — the primitive paints SVG + drop-shadow
 * filters, which need real colours, not CSS vars). The Sovereign core is left to the ego theme
 * (crimson); these tint the *halos* of the surrounding stars.
 */
export const TIER_HUE: Record<RosterGroup, string> = {
  sovereign: '#e0564a', // --ae-color-crimson-400 (the centre / ego)
  adept: '#2dd4bf', // --ae-brand teal — delegated specialists
  bond: '#c9a96e', // --ae-brand-gold — trusted peers
  guild: '#7a7268', // --ae-brand-ash — the muted outer network
}

/** Inner = the Sovereign and the Adepts it delegates to. Outer = everyone reached through a bond. */
export function orbitOf(group: RosterGroup): Orbit {
  return group === 'sovereign' || group === 'adept' ? 'inner' : 'outer'
}

export function tierGroupOf(tier: string): RosterGroup {
  if (tier === 'sovereign') return 'sovereign'
  if (tier === 'adept') return 'adept'
  return 'guild'
}

/** Covenant-at-a-glance line: "Inner · active" — orbit (reasoning locality) + lifecycle status. */
function metaLine(group: RosterGroup, status?: string): string {
  const orbit = orbitOf(group) === 'inner' ? 'Inner' : 'Outer'
  return status ? `${orbit} · ${status}` : orbit
}

function star(f: Familiar, group: RosterGroup): ConstellationNode {
  return {
    id: f.id,
    label: f.name,
    group,
    // Colour the halo by tier; the core stays parchment (theme.star). The Sovereign centre is the
    // exception — it wears the ego theme (crimson core + halo) and ignores this.
    glow: TIER_HUE[group],
    // Inner stars burn brighter; the outer bonded network recedes — the visible Inner/Outer split.
    brightness: orbitOf(group) === 'inner' ? 0.85 : 0.5,
    meta: metaLine(group, f.status),
  }
}

export interface RosterInput {
  /** The familiar at the centre — normally the keeper's Sovereign. */
  sovereign: Familiar
  /** Adepts the keeper owns; only wired as delegated when the centre is a Sovereign. */
  adepts?: Familiar[]
  /** Bonds touching the centre (either direction), from GET /bonds. */
  bonds?: Bond[]
  /** Every familiar the keeper owns, for naming bonded counterparts we happen to own. */
  familiarsById?: Record<string, Familiar>
}

export interface Roster {
  nodes: ConstellationNode[]
  edges: ConstellationEdge[]
  /** Convenience flags for the empty/sparse state. */
  adeptCount: number
  bondCount: number
}

export interface KeeperRosterInput {
  /** Every familiar the keeper owns. */
  familiars: Familiar[]
  /** Bonds touching the centre familiar (either direction), from GET /bonds. */
  bonds?: Bond[]
}

export interface KeeperRoster extends Roster {
  /** The star that holds the centre — the keeper's Sovereign when one exists. */
  centerId: string | null
}

/**
 * The keeper's whole household as one sky — the home surface. The Sovereign (when the keeper has
 * one) holds the centre; every owned familiar is a star grouped by tier; Adepts hang off the
 * Sovereign by delegation (canon: Adepts are reachable only through the Sovereign) and bonds add
 * their counterparts. A familiar with no recorded relation still renders — it drifts to the outer
 * ring rather than being hidden.
 */
export function buildKeeperRoster(input: KeeperRosterInput): KeeperRoster {
  const { familiars, bonds = [] } = input
  const center = familiars.find((f) => f.tier === 'sovereign') ?? familiars[0] ?? null
  if (!center) return { nodes: [], edges: [], adeptCount: 0, bondCount: 0, centerId: null }

  const familiarsById = Object.fromEntries(familiars.map((f) => [f.id, f]))
  const roster = buildFamiliarRoster({
    sovereign: center,
    adepts: center.tier === 'sovereign' ? familiars.filter((f) => f.tier === 'adept') : [],
    bonds,
    familiarsById,
  })

  // Owned familiars the centre has no relation to (guilds, spare familiars) still belong in the sky.
  const seen = new Set(roster.nodes.map((n) => n.id))
  for (const f of familiars) {
    if (seen.has(f.id)) continue
    roster.nodes.push(star(f, tierGroupOf(f.tier)))
    seen.add(f.id)
  }

  return { ...roster, centerId: center.id }
}

/**
 * Assemble the ecosystem. Always returns at least the centre (never crashes on missing data):
 *   • centre = the Sovereign (also the ego/focus).
 *   • each Adept → a star, edge Sovereign→Adept kind 'delegates'.
 *   • each Bond  → the counterpart star, edge Sovereign→counterpart kind 'bond'.
 * A familiar that is both an Adept and a bonded counterpart appears once (the bond adds only an edge).
 */
export function buildFamiliarRoster(input: RosterInput): Roster {
  const { sovereign, adepts = [], bonds = [], familiarsById = {} } = input
  const nodes: ConstellationNode[] = []
  const edges: ConstellationEdge[] = []
  const seen = new Set<string>()

  const add = (node: ConstellationNode) => {
    if (seen.has(node.id)) return
    seen.add(node.id)
    nodes.push(node)
  }

  // Centre — grouped by its true tier (an Adept-centred sky stays labelled adept).
  add(star(sovereign, tierGroupOf(sovereign.tier)))

  // Adepts — only meaningful when the centre delegates (a Sovereign). Skip the centre itself.
  let adeptCount = 0
  for (const a of adepts) {
    if (a.id === sovereign.id) continue
    add(star(a, 'adept'))
    edges.push({ from: sovereign.id, to: a.id, label: 'delegates', kind: 'delegates' })
    adeptCount += 1
  }

  // Bonds — the counterpart is whichever side isn't the centre.
  let bondCount = 0
  for (const b of bonds) {
    const counterpartId =
      b.initiator_familiar_id === sovereign.id ? b.target_familiar_id : b.initiator_familiar_id
    if (counterpartId === sovereign.id) continue
    const owned = familiarsById[counterpartId]
    if (!seen.has(counterpartId)) {
      if (owned) {
        add(star(owned, tierGroupOf(owned.tier) === 'sovereign' ? 'bond' : tierGroupOf(owned.tier)))
      } else {
        // A familiar bound from another keeper — we hold only its id.
        add({
          id: counterpartId,
          label: 'Bonded familiar',
          group: 'bond',
          glow: TIER_HUE.bond,
          brightness: 0.45,
          meta: metaLine('bond'),
        })
      }
    }
    edges.push({ from: sovereign.id, to: counterpartId, label: 'bond', kind: 'bond' })
    bondCount += 1
  }

  return { nodes, edges, adeptCount, bondCount }
}
